import { MAINTENANCE_CONSTANTS } from '@/constants/residents';

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Hook providing validation logic for maintenance forms
 * Consolidates duplicate validation from mobile and desktop forms
 */
export const useMaintenanceValidation = () => {
  const validateSubcategory = (
    requestType: string, 
    subCategoryOptions: string[], 
    subCategory: string
  ): ValidationResult => {
    if (requestType === 'maintenance' && subCategoryOptions.length > 0 && !subCategory) {
      return { valid: false, message: 'Please select a subcategory.' };
    }
    return { valid: true };
  };

  const validateSeverity = (severity: string): ValidationResult => {
    if (!severity) {
      return { valid: false, message: 'Please select how this affects you.' };
    }
    return { valid: true };
  };

  const validateTitle = (title: string): ValidationResult => {
    if (!title.trim()) {
      return { valid: false, message: 'Please enter a brief title.' };
    }
    return { valid: true };
  };

  const validateTimeSlots = (
    permissionChoice: string | null, 
    timeSlots: string[]
  ): ValidationResult => {
    if (permissionChoice === 'no') {
      const filledSlots = timeSlots.filter(s => s.trim()).length;
      if (filledSlots < MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS) {
        return { 
          valid: false, 
          message: `Please select at least ${MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS} time slots.` 
        };
      }
    }
    return { valid: true };
  };

  const validateForm = (params: {
    requestType: string;
    subCategoryOptions: string[];
    subCategory: string;
    severity: string;
    title: string;
    permissionChoice: string | null;
    timeSlots: string[];
  }): ValidationResult => {
    // Validate in order of form appearance
    const subcategoryResult = validateSubcategory(
      params.requestType, 
      params.subCategoryOptions, 
      params.subCategory
    );
    if (!subcategoryResult.valid) return subcategoryResult;

    const severityResult = validateSeverity(params.severity);
    if (!severityResult.valid) return severityResult;

    const titleResult = validateTitle(params.title);
    if (!titleResult.valid) return titleResult;

    const timeSlotsResult = validateTimeSlots(params.permissionChoice, params.timeSlots);
    if (!timeSlotsResult.valid) return timeSlotsResult;

    return { valid: true };
  };

  return {
    validateSubcategory,
    validateSeverity,
    validateTitle,
    validateTimeSlots,
    validateForm,
  };
};
